import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '../../../../../../components/ui/dialog';
import { Button } from '../../../../../../components/ui/button';
import { FileUpload } from '../../../../../../components/ui/FileUpload';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../../components/ui/select';
import { Label } from '../../../../../../components/ui/label';
import { Upload, FileText, X, CheckCircle, AlertTriangle, Cpu } from 'lucide-react';
import weldAxiosConfig from '../../../../../../redux/WeldData/axiosConfig';
import customNotification from '../../../../../../components/ui/custom-notification';
import { logWeldLogUploaded } from '../../../../utils/productionAudit';
import { useProductionModalAudit } from '../../../../../audit/useProductionModalAudit';
import { WeldingSystemConfigEnum, isWeldingSystemConfig } from './weldingSystemConfig';

interface MachineTypeOption {
  value: WeldingSystemConfigEnum;
  label: string;
}

const MACHINE_TYPE_OPTIONS: MachineTypeOption[] = [
  { value: WeldingSystemConfigEnum.P600Z, label: 'P600Z' },
  { value: WeldingSystemConfigEnum.M500, label: 'M500' },
  { value: WeldingSystemConfigEnum.P625, label: 'P625' },
  { value: WeldingSystemConfigEnum.IWM, label: 'IWM' },
];

/** Excel header → API / internal field (from manual weld / TLOG spreadsheet). */
const HEADER_TLOG_DATA: Record<string, string> = {
  welduid_s_event_torch: 'File Name',
  weld_number: 'Weld ID',
  zone_name: 'Pass Name',
  /** Sample end — backend weld_time / t_log uses Time vs S_Time. */
  time: 'Finish time',
  job_number: 'Job Number',
  travel_speed: 'Travel Spe',
  current: 'Record Current',
  volts: 'Record Voltage',
  heat: 'Heat Input(Kj/in)',
  s_time: 'Start Time',
  e_time: 'Finish time',
  /** Pass arc length in seconds (same on every row of a pass). */
  duration: 'Duration (s)',
  current_min: 'Current Min',
  current_avg: 'Current Avg',
  current_max: 'Current Max',
  voltage_min: 'Voltage Min',
  voltage_avg: 'Voltage Avg',
  voltage_max: 'Voltage Max',
  weld_time: 'Duration (s)',
};

const WELD_BULK_UPLOAD_ENDPOINT ='/log/bulkCreateUpdateLogs';
const TLOG_BULK_UPLOAD_ENDPOINT = '/log/bulkCreateUpdateTLogs';

interface UploadTlogExcelDataDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete?: () => void;
  project?: any;
}

export interface TlogExcelRow {
  project_id: number;
  welduid_s_event_torch: string;
  weld_number: string;
  zone_name: string;
  time: number | string;
  job_number: string;
  travel_speed: string | number;
  current: string | number;
  volts: string | number;
  heat: string | number;
  s_time: number | string;
  e_time: number | string;
  duration: string | number;
  event: number;
  [key: string]: unknown;
}

interface PreviewRow {
  weldNumber: string;
  jobNumber: string;
  passName: string;
  volts: string;
  current: string;
  travelSpeed: string;
}

interface WeldUploadPayload {
  project_id: number;
  machine_type: string;
  records: Array<Record<string, unknown>>;
}

const parseNumber = (value: unknown, defaultValue = 0): number => {
  if (value == null || value === '') return defaultValue;
  const n = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : defaultValue;
};

const parseString = (value: unknown, defaultValue = ''): string =>
  String(value ?? defaultValue).trim();

/**
 * Keep Excel decimals as the user sees them (e.g. 31.82 → "31.82").
 * Avoids float noise (31.819999999…) and does not round to 1 decimal.
 */
const preserveExcelDecimalString = (value: unknown, defaultValue = ''): string => {
  if (value == null || value === '') return defaultValue;
  if (typeof value === 'string') {
    const trimmed = value.trim().replace(/,/g, '');
    if (!trimmed) return defaultValue;
    // Keep the typed/displayed string when it is already a decimal literal
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) return trimmed;
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return trimmed;
    return parseFloat(n.toFixed(10)).toString();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    // Strip binary float artifacts without reducing visible precision (31.82 stays 31.82)
    return parseFloat(value.toFixed(10)).toString();
  }
  return parseString(value, defaultValue);
};

const toUnixTimestamp = (
  val: unknown,
  baseDateFromFile: { year: number; month: number; day: number } | null
): number => {
  if (val === undefined || val === null || val === '') return 0;
  const n = Number(val);
  if (!Number.isNaN(n) && n > 1e9 && n < 1e11) return Math.floor(n);
  if (!Number.isNaN(n) && n > 1000 && n < 100000) {
    const excelEpoch = new Date(1899, 11, 30);
    const d = new Date(excelEpoch.getTime() + n * 86400000);
    return Math.floor(d.getTime() / 1000);
  }
  const s = String(val).trim();
  if (!s) return 0;
  let date = new Date(s);
  if (!Number.isNaN(date.getTime())) return Math.floor(date.getTime() / 1000);
  const [h, m, sec] = s.split(/[:\s]/).map(Number);
  if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
    if (baseDateFromFile) {
      date = new Date(
        baseDateFromFile.year,
        baseDateFromFile.month,
        baseDateFromFile.day,
        h,
        Number.isNaN(m) ? 0 : m,
        Number.isNaN(sec) ? 0 : sec,
        0
      );
    } else {
      date = new Date();
      date.setHours(h, Number.isNaN(m) ? 0 : m, Number.isNaN(sec) ? 0 : sec, 0);
    }
    return Math.floor(date.getTime() / 1000);
  }
  return 0;
};

const baseDateFromFileName = (
  fileName: string
): { year: number; month: number; day: number } | null => {
  const dateMatch = fileName.match(/(\d{8})/);
  if (!dateMatch) return null;
  const ymd = dateMatch[1];
  const year = parseInt(ymd.slice(0, 4), 10);
  const month = parseInt(ymd.slice(4, 6), 10) - 1;
  const day = parseInt(ymd.slice(6, 8), 10);
  if (year >= 1900 && year <= 2100 && month >= 0 && month <= 11 && day >= 1 && day <= 31) {
    return { year, month, day };
  }
  return null;
};

/**
 * Device/TXT Time is unix_seconds/1000 (e.g. 1777664.786).
 * Backend mapper stores Time*1000 → unix seconds on pass_view / t_log;
 * UI does new Date(s_time * 1000). Full unix (1789442691) breaks both weld_time and display.
 */
const toDeviceTimeStr = (unixSeconds: number): string => {
  if (!(unixSeconds > 0) || !Number.isFinite(unixSeconds)) return '';
  return String(Math.round(unixSeconds * 1000) / 1e6);
};

/**
 * Parse TLOG Excel/CSV rows, then expand into S/T/C like TXT upload:
 * group by Start Time (s_time) into passes → one S, many T, one C per pass.
 */
export function parseTlogExcelToStcRecords(
  rows: TlogExcelRow[],
  projectId: number,
  machineType: string
): { payload: WeldUploadPayload; previewRows: PreviewRow[]; tlogItems: Array<Record<string, unknown>> } {
  const records: Array<Record<string, unknown>> = [];
  const previewRows: PreviewRow[] = [];
  const tlogItems: Array<Record<string, unknown>> = [];

  const rowMeta = rows.map((tlogRow, index) => {
    const jobNumber = parseString(tlogRow.job_number) || 'JOB-1';
    const weldNumber = parseString(tlogRow.weld_number);
    /**
     * Use Excel Pass Name as-is. Normalizing Fill 1 / Fill 2 → "Fill" merged many
     * passes into ~4 S/C (Root/Hot/Fill/Cap). Distinct Pass Name → distinct S/C.
     */
    const rawZoneName = parseString(tlogRow.zone_name) || 'UNKNOWN';
    const passName = rawZoneName;
    const fileName = parseString(tlogRow.welduid_s_event_torch);
    const weldUidBase =
      fileName ||
      (jobNumber && weldNumber ? `${jobNumber}_${weldNumber}` : `ROW_${index + 1}`);
    const sTimeUnix =
      typeof tlogRow.s_time === 'number' ? tlogRow.s_time : parseNumber(tlogRow.s_time, 0);
    const eTimeUnix =
      typeof tlogRow.e_time === 'number' ? tlogRow.e_time : parseNumber(tlogRow.e_time, 0);
    const timeUnix =
      typeof tlogRow.time === 'number' ? tlogRow.time : parseNumber(tlogRow.time, eTimeUnix || sTimeUnix);
    /** Prefer Finish; fall back to Start + Duration (s) when Finish missing. */
    const durationSec = parseNumber(tlogRow.duration, 0);
    const endUnix =
      eTimeUnix > 0
        ? eTimeUnix
        : timeUnix > 0
          ? timeUnix
          : sTimeUnix > 0 && durationSec > 0
            ? sTimeUnix + durationSec
            : 0;
    const sTimeStr = toDeviceTimeStr(sTimeUnix);
    const eTimeStr = toDeviceTimeStr(endUnix);
    const timeStr = toDeviceTimeStr(timeUnix > 0 ? timeUnix : endUnix) || eTimeStr;
    /** One distinct Pass Name → one S + many T + one C (do not use File Name / Start Time). */
    const segmentKey = [weldNumber || 'NO_WELD', jobNumber, rawZoneName].join('|');

    return {
      tlogRow,
      index,
      jobNumber,
      weldNumber,
      passName,
      rawZoneName,
      weldUidBase,
      sTimeUnix,
      eTimeUnix: endUnix,
      timeUnix: timeUnix > 0 ? timeUnix : endUnix,
      sTimeStr,
      eTimeStr,
      timeStr,
      durationSec,
      segmentKey,
    };
  });

  const segmentMap = new Map<string, typeof rowMeta>();
  for (const meta of rowMeta) {
    const list = segmentMap.get(meta.segmentKey);
    if (list) list.push(meta);
    else segmentMap.set(meta.segmentKey, [meta]);
  }
  /** Stable order so Event numbers (and welduid keys) match on re-upload. */
  const segments = Array.from(segmentMap.values()).sort((a, b) => {
    const keyA = `${a[0]?.weldUidBase || ''}|${a[0]?.passName || ''}|${a[0]?.jobNumber || ''}`;
    const keyB = `${b[0]?.weldUidBase || ''}|${b[0]?.passName || ''}|${b[0]?.jobNumber || ''}`;
    return keyA.localeCompare(keyB);
  });

  segments.forEach((segment, segmentIndex) => {
    const first = segment[0];
    const {
      jobNumber,
      weldNumber,
      passName,
      weldUidBase,
    } = first;

    const eventNo = String(segmentIndex + 1);

    let minStartUnix = Infinity;
    let minStartStr = '';
    let maxEndUnix = -Infinity;
    let maxEndStr = '';
    let lastTimeStr = '';

    segment.forEach((meta) => {
      if (meta.sTimeUnix > 0 && meta.sTimeUnix < minStartUnix) {
        minStartUnix = meta.sTimeUnix;
        minStartStr = meta.sTimeStr;
      } else if (!minStartStr && meta.sTimeStr) {
        minStartStr = meta.sTimeStr;
      }
      if (meta.eTimeUnix > maxEndUnix) {
        maxEndUnix = meta.eTimeUnix;
        maxEndStr = meta.eTimeStr || meta.timeStr;
      } else if (meta.timeUnix > maxEndUnix) {
        maxEndUnix = meta.timeUnix;
        maxEndStr = meta.timeStr;
      }
      if (meta.timeStr) lastTimeStr = meta.timeStr;
    });

    const instrumentStartStr =
      minStartStr || first.sTimeStr || first.timeStr || '';

    /** Prefer latest Finish; else Start + Duration (s) from Excel. */
    let cTimeStr = maxEndStr || lastTimeStr || '';
    if (
      (!cTimeStr || cTimeStr === instrumentStartStr) &&
      minStartUnix > 0 &&
      Number.isFinite(minStartUnix)
    ) {
      const passDurationSec =
        first.durationSec > 0
          ? first.durationSec
          : segment.reduce((max, m) => (m.durationSec > max ? m.durationSec : max), 0);
      if (passDurationSec > 0) {
        cTimeStr = toDeviceTimeStr(minStartUnix + passDurationSec);
      }
    }
    if (!cTimeStr) cTimeStr = instrumentStartStr;

    const weldUid =
      first.weldUidBase ||
      (jobNumber && weldNumber ? `${jobNumber}_${weldNumber}` : `${weldNumber || 'WELD'}_${passName}`);

    const dateParts =
      minStartUnix > 0 && Number.isFinite(minStartUnix)
        ? (() => {
            const d = new Date(minStartUnix * 1000);
            return {
              Year: String(d.getFullYear()),
              Month: String(d.getMonth() + 1),
              Day: String(d.getDate()),
              Hour: String(d.getHours()),
              Minute: String(d.getMinutes()),
              Second: String(d.getSeconds()),
            };
          })()
        : { Year: '', Month: '', Day: '', Hour: '', Minute: '', Second: '' };

    /** One S per pass/segment (TXT pattern). */
    records.push({
      Record: 'S',
      Event: eventNo,
      Time: instrumentStartStr,
      S_Time: instrumentStartStr,
      Edge_UID: '',
      Weld_UID: weldUid,
      Weld_Type: machineType,
      Bug_number: '',
      Station_number: '',
      Bug_type: '',
      Pipe_diameter: '',
      Band_diameter: '',
      Logging_distance: '',
      Job_number: jobNumber,
      Weld_number: weldNumber,
      Lead_wire_diameter: '',
      Trail_wire_diameter: '',
      ...dateParts,
    });

    /** One T per Excel row in this pass. */
    segment.forEach((meta) => {
      const tEventNo = String(meta.index + 1);
      /** Sample end time (Finish) — matches TXT where Time > Start Time. */
      const rowTimeStr = meta.timeStr || meta.eTimeStr || instrumentStartStr;

      records.push({
        Record: 'T',
        Event: tEventNo,
        Time: rowTimeStr,
        S_Event: eventNo,
        S_Time: instrumentStartStr,
        Edge_UID: '',
        Weld_UID: weldUid,
        Station_number: '',
        Weld_Type: machineType,
        Job_number: meta.jobNumber || jobNumber,
        jobNumber: meta.jobNumber || jobNumber,
        Tilt: '',
        Travel_speed: preserveExcelDecimalString(meta.tlogRow.travel_speed),
        Distance: '',
        Lead_pass_name: meta.passName || passName,
        Lead_volts: preserveExcelDecimalString(meta.tlogRow.volts),
        Lead_amps: preserveExcelDecimalString(meta.tlogRow.current),
        Lead_wire_speed: '',
        Lead_oscillate_width: '',
        Lead_target: '',
        Lead_horizontal_bias: '',
        Lead_frequency: '',
        Lead_total_wire_consumed: '',
        Lead_heat: preserveExcelDecimalString(meta.tlogRow.heat),
        Trail_pass_name: meta.passName || passName,
        Trail_volts: '',
        Trail_amps: '',
        Trail_wire_speed: '',
        Trail_oscillate_width: '',
        Trail_target: '',
        Trail_horizontal_bias: '',
        Trail_frequency: '',
        Trail_total_wire_consumed: '',
        Trail_heat: '',
      });

      tlogItems.push({
        weld_number: meta.weldNumber || weldNumber,
        project_id: projectId,
        pipe_id: 0,
        time: meta.timeUnix || meta.tlogRow.time,
        s_time: meta.sTimeUnix || meta.tlogRow.s_time,
        e_time: meta.eTimeUnix || meta.tlogRow.e_time,
        duration: meta.durationSec || meta.tlogRow.duration,
        pass_name: meta.passName || passName,
        pass: meta.passName || passName,
        zone_name: meta.tlogRow.zone_name,
        job_number: meta.jobNumber || jobNumber,
        travel_speed: preserveExcelDecimalString(meta.tlogRow.travel_speed),
        current: preserveExcelDecimalString(meta.tlogRow.current),
        volts: preserveExcelDecimalString(meta.tlogRow.volts),
        heat: preserveExcelDecimalString(meta.tlogRow.heat),
        event: meta.tlogRow.event,
        weld_uid: weldUid,
        welduid_s_event_torch: weldUid,
        torch: 'Lead',
      });

      if (previewRows.length < 8) {
        previewRows.push({
          weldNumber: meta.weldNumber || weldNumber,
          jobNumber: meta.jobNumber || jobNumber,
          passName: meta.passName || passName,
          volts: parseString(meta.tlogRow.volts),
          current: parseString(meta.tlogRow.current),
          travelSpeed: parseString(meta.tlogRow.travel_speed),
        });
      }
    });

    /** One C per pass/segment — end at max Finish time (or Start + Duration). */
    records.push({
      Record: 'C',
      Event: eventNo,
      Time: cTimeStr,
      S_Event: eventNo,
      S_Time: instrumentStartStr,
      Edge_UID: '',
      Weld_UID: weldUid,
      Weld_Type: machineType,
      Gps: {
        Latitude: '',
        Longitude: '',
        Confidence: '',
        HDOP: '',
      },
    });
  });

  return {
    payload: {
      project_id: projectId,
      machine_type: machineType,
      records,
    },
    previewRows,
    tlogItems,
  };
}

async function readSpreadsheetRows(file: File, projectId: number): Promise<TlogExcelRow[]> {
  const lowerName = (file.name || '').toLowerCase();
  let workbook: XLSX.WorkBook;
  if (lowerName.endsWith('.csv')) {
    let csvText = await file.text();
    if (csvText.charCodeAt(0) === 0xfeff) csvText = csvText.slice(1);
    workbook = XLSX.read(csvText, { type: 'string' });
  } else {
    const arrayBuffer = await file.arrayBuffer();
    // cellText/cellNF populate cell.w (display text) — used for heat decimals
    workbook = XLSX.read(arrayBuffer, { type: 'array', cellText: true, cellNF: true });
  }

  const firstSheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[firstSheetName];
  if (!worksheet) {
    throw new Error('No data found in file');
  }

  // Keep raw numbers for times; heat uses cell.w display text when present
  const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) as unknown[][];
  if (data.length < 2) {
    throw new Error('File must have at least a header row and one data row');
  }

  const headers = (data[0] as unknown[]).map((h) => String(h || '').trim());
  const heatColIndex = headers.findIndex((h) => {
    const n = h.toLowerCase().replace(/\s+/g, '');
    return (
      n.includes('heatinput') ||
      n === 'heat' ||
      h === 'Heat Input(Kj/in)' ||
      h === HEADER_TLOG_DATA.heat
    );
  });

  const cellDisplayOrRaw = (rowIndex0: number, colIndex: number, fallback: unknown): unknown => {
    if (colIndex < 0) return fallback;
    const addr = XLSX.utils.encode_cell({ r: rowIndex0, c: colIndex });
    const cell = worksheet[addr];
    if (!cell) return fallback;
    // Prefer Excel-formatted text so 31.82 stays "31.82" (not float noise / rounding)
    if (cell.w != null && String(cell.w).trim() !== '') return String(cell.w).trim();
    return cell.v !== undefined && cell.v !== null ? cell.v : fallback;
  };

  const processedData: TlogExcelRow[] = [];

  // Skip row 1 if it is a sub-header (same pattern as NDT TLOG parser: start at i = 2 when present)
  const startRow = data.length > 2 ? 2 : 1;

  for (let i = startRow; i < data.length; i += 1) {
    const row = data[i] as unknown[];
    if (!row) continue;

    const rowData: Record<string, unknown> = {};
    headers.forEach((header, index) => {
      rowData[header] = row[index] !== undefined && row[index] !== null ? row[index] : '';
    });

    // Override heat with Excel display text when available
    if (heatColIndex >= 0) {
      const heatDisplayed = cellDisplayOrRaw(i, heatColIndex, row[heatColIndex]);
      const heatHeader = headers[heatColIndex];
      if (heatHeader) rowData[heatHeader] = heatDisplayed;
    }

    const hasAnyData = headers.some((_, idx) => {
      const v = row[idx];
      return v !== undefined && v !== null && String(v).trim() !== '';
    });
    if (!hasAnyData) continue;

    const getCell = (excelHeader: string, apiKey: string) => {
      const v = rowData[excelHeader];
      if (v !== undefined && v !== null && String(v).trim() !== '') return v;
      const camel = apiKey.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
      return rowData[camel] ?? '';
    };

    const tlogData: TlogExcelRow = {
      project_id: projectId,
      welduid_s_event_torch: '',
      weld_number: '',
      zone_name: '',
      time: '',
      job_number: '',
      travel_speed: '',
      current: '',
      volts: '',
      heat: '',
      s_time: '',
      e_time: '',
      duration: '',
      event: 0,
    };

    Object.entries(HEADER_TLOG_DATA).forEach(([apiKey, excelHeader]) => {
      const cellVal = getCell(excelHeader, apiKey);
      // Heat / process params: keep Excel decimals exactly (no Number() round-trip)
      if (
        apiKey === 'heat' ||
        apiKey === 'current' ||
        apiKey === 'volts' ||
        apiKey === 'travel_speed' ||
        apiKey === 'current_min' ||
        apiKey === 'current_avg' ||
        apiKey === 'current_max' ||
        apiKey === 'voltage_min' ||
        apiKey === 'voltage_avg' ||
        apiKey === 'voltage_max'
      ) {
        (tlogData as any)[apiKey] = preserveExcelDecimalString(cellVal);
      } else {
        (tlogData as any)[apiKey] = cellVal;
      }
    });

    // Fallback heat headers if the sheet uses a slightly different label
    if (!parseString(tlogData.heat)) {
      const heatAlt =
        rowData['Heat Input(Kj/in)'] ??
        rowData['Heat Input (Kj/in)'] ??
        rowData['Heat Input(KJ/in)'] ??
        rowData['Heat Input'] ??
        rowData['Heat'] ??
        rowData.heat ??
        '';
      tlogData.heat = preserveExcelDecimalString(heatAlt);
    }

    if (!parseString(tlogData.job_number)) {
      tlogData.job_number = 'JOB-1';
    }

    if (!parseString(tlogData.weld_number)) {
      const weldIdVal =
        rowData['Weld Number'] ?? rowData['Weld Id'] ?? rowData['weld id'] ?? rowData.weldNumber ?? '';
      tlogData.weld_number = parseString(weldIdVal);
    }

    if (typeof tlogData.welduid_s_event_torch === 'string') {
      tlogData.welduid_s_event_torch = tlogData.welduid_s_event_torch.replace(/\.txt$/i, '');
    }

    const baseDate = baseDateFromFileName(String(tlogData.welduid_s_event_torch || ''));
    if (tlogData.time !== undefined && tlogData.time !== '') {
      tlogData.time = toUnixTimestamp(tlogData.time, baseDate);
    }
    if (tlogData.s_time !== undefined && tlogData.s_time !== '') {
      tlogData.s_time = toUnixTimestamp(tlogData.s_time, baseDate);
    }
    if (tlogData.e_time !== undefined && tlogData.e_time !== '') {
      tlogData.e_time = toUnixTimestamp(tlogData.e_time, baseDate);
    }

    const sTimeNum = typeof tlogData.s_time === 'number' ? tlogData.s_time : 0;
    const startDate = new Date(sTimeNum * 1000);
    tlogData.event =
      startDate.getHours() * 3600 + startDate.getMinutes() * 60 + startDate.getSeconds();

    processedData.push(tlogData);
  }

  return processedData;
}

/**
 * Upload TLOG Excel/CSV → convert each row to S/T/C weld-log records (+ TLOG bulk payload).
 * Does not replace UploadWeldDataDialog (TXT path).
 */
export const UploadTlogExcelDataDialog: React.FC<UploadTlogExcelDataDialogProps> = ({
  isOpen,
  onClose,
  onUploadComplete,
  project,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [payload, setPayload] = useState<WeldUploadPayload | null>(null);
  const [tlogItems, setTlogItems] = useState<Array<Record<string, unknown>>>([]);
  const [excelRowCount, setExcelRowCount] = useState(0);
  const [machineType, setMachineType] = useState<WeldingSystemConfigEnum | ''>('');

  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setIsProcessing(false);
      setIsUploading(false);
      setParseError(null);
      setPreviewRows([]);
      setPayload(null);
      setTlogItems([]);
      setExcelRowCount(0);
      setMachineType('');
    }
  }, [isOpen]);

  const projectId = useMemo(() => {
    const value = project?.project?.projectId ?? project?.id ?? project?.projectId ?? 0;
    return parseNumber(String(value), 0);
  }, [project]);

  const projectName = useMemo(
    () => project?.project?.projectName ?? project?.projectName ?? project?.name ?? '',
    [project]
  );

  useProductionModalAudit(
    isOpen,
    'Upload TLOG Data',
    projectId > 0 ? projectId : undefined,
    projectName || null
  );

  const handleRemoveFile = () => {
    setFile(null);
    setParseError(null);
    setPreviewRows([]);
    setPayload(null);
    setTlogItems([]);
    setExcelRowCount(0);
  };

  const processFile = async (selectedFile: File, type: WeldingSystemConfigEnum) => {
    setIsProcessing(true);
    setParseError(null);
    setPreviewRows([]);
    setPayload(null);
    setTlogItems([]);
    setExcelRowCount(0);

    try {
      const rows = await readSpreadsheetRows(selectedFile, projectId);
      if (rows.length === 0) {
        const msg = 'No data rows found in the uploaded spreadsheet.';
        setParseError(msg);
        customNotification.error(msg);
        return;
      }

      const result = parseTlogExcelToStcRecords(rows, projectId, type);
      setPayload(result.payload);
      setPreviewRows(result.previewRows);
      setTlogItems(result.tlogItems);
      setExcelRowCount(rows.length);
      customNotification.success(
        `Processed ${rows.length} Excel row(s) → ${result.payload.records.filter((r) => r.Record === 'S').length} S / ${result.payload.records.filter((r) => r.Record === 'T').length} T / ${result.payload.records.filter((r) => r.Record === 'C').length} C`
      );
    } catch (error: any) {
      const msg = error?.message || 'Failed to process spreadsheet file';
      setParseError(msg);
      customNotification.error(msg);
    } finally {
      setIsProcessing(false);
    }
  };

  const isAcceptedUpload = (f: File): boolean => {
    const lowerName = (f.name || '').toLowerCase();
    if (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls') || lowerName.endsWith('.csv')) {
      return true;
    }
    const mime = (f.type || '').toLowerCase();
    return (
      mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      mime === 'application/vnd.ms-excel' ||
      mime === 'text/csv' ||
      mime === 'application/csv'
    );
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (!isWeldingSystemConfig(machineType)) {
      customNotification.error('Please select a welding machine before uploading the file.');
      return;
    }
    const selectedFile = files[0];
    if (!isAcceptedUpload(selectedFile)) {
      customNotification.error('Please upload an Excel or CSV file (.xlsx, .xls, or .csv)');
      return;
    }
    setFile(selectedFile);
    void processFile(selectedFile, machineType);
  };

  const handleMachineTypeChange = (value: string) => {
    if (!isWeldingSystemConfig(value) || value === machineType) return;
    setMachineType(value);
    if (file || payload || previewRows.length > 0 || parseError) {
      handleRemoveFile();
      customNotification.info('Machine type changed — please upload the spreadsheet again.');
    }
  };

  const handleUpload = async () => {
    if (!payload || payload.records.length === 0) {
      customNotification.error('No payload generated. Upload a valid spreadsheet first.');
      return;
    }
    if (!projectId) {
      customNotification.error('Project ID is missing. Select a project and try again.');
      return;
    }

    setIsUploading(true);
    try {
      const response = await weldAxiosConfig.post(WELD_BULK_UPLOAD_ENDPOINT, payload);
      if (response.status < 200 || response.status >= 300) {
        throw new Error('Failed to upload weld S/T/C records');
      }

      // if (tlogItems.length > 0) {
      //   await weldAxiosConfig.post(TLOG_BULK_UPLOAD_ENDPOINT, {
      //     data: tlogItems,
      //     trigger_recalculation: true,
      //   });
      // }

      const weldNumbers = Array.from(
        new Set(
          payload.records
            .map((record) => String(record.Weld_number ?? record.weld_number ?? '').trim())
            .filter(Boolean)
        )
      );
      const jobNumbers = Array.from(
        new Set(
          payload.records
            .map((record) => String(record.Job_number ?? record.jobNumber ?? '').trim())
            .filter(Boolean)
        )
      );

      logWeldLogUploaded({
        projectId,
        projectName,
        fileName: file?.name ?? 'tlog-excel',
        machineType: payload.machine_type,
        recordCount: payload.records.length,
        tRecordCount: payload.records.filter((record) => record.Record === 'T').length,
        sRecordCount: payload.records.filter((record) => record.Record === 'S').length,
        cRecordCount: payload.records.filter((record) => record.Record === 'C').length,
        weldNumbers,
        jobNumbers,
      });

      customNotification.success(
        `Uploaded ${excelRowCount} Excel row(s) as ${payload.records.length} S/T/C records`
      );
      onUploadComplete?.();
      onClose();
    } catch (error: any) {
      customNotification.error(
        error?.response?.data?.message || error?.message || 'Failed to upload TLOG Excel data.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    if (isUploading) return;
    handleRemoveFile();
    onClose();
  };

  const sCount = payload?.records.filter((r) => r.Record === 'S').length ?? 0;
  const tCount = payload?.records.filter((r) => r.Record === 'T').length ?? 0;
  const cCount = payload?.records.filter((r) => r.Record === 'C').length ?? 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[95vw] max-w-[1200px] max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden bg-white">
        <div className="flex items-center gap-3 border-b border-[#E2E5EA] px-6 py-4 pr-14 flex-shrink-0">
          <div className="flex-shrink-0 w-10 h-10 bg-hero-red-10 rounded-full flex items-center justify-center">
            <Upload className="h-5 w-5 text-hero-red" />
          </div>
          <DialogTitle className="font-heading text-[18px] font-semibold text-blueprint-blue leading-tight">
            Upload TLOG Excel Data
          </DialogTitle>
        </div>

        <div className="space-y-6 p-6 flex-1 overflow-y-auto overflow-x-hidden bg-white">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-hero-red text-white rounded-full flex items-center justify-center text-[12px] font-medium">
                1
              </div>
              <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">
                Select Welding Machine
              </h3>
            </div>
            <div className="max-w-xs space-y-1.5">
              <Label htmlFor="tlog-welding-machine-type" className="text-[12px] font-medium text-text-grey">
                Welding system
              </Label>
              <Select
                value={machineType}
                onValueChange={handleMachineTypeChange}
                disabled={isProcessing || isUploading}
              >
                <SelectTrigger id="tlog-welding-machine-type" className="h-9 bg-white text-sm">
                  <div className="flex items-center gap-2">
                    <Cpu className="h-4 w-4 text-heritage-grey" />
                    <SelectValue placeholder="Select machine" />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {MACHINE_TYPE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span className="text-[13px] font-medium text-text-grey">{opt.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-hero-red text-white rounded-full flex items-center justify-center text-[12px] font-medium">
                2
              </div>
              <h3 className="font-heading text-[14px] font-semibold text-blueprint-blue">
                Upload Excel / CSV
              </h3>
            </div>
            <p className="text-[13px] text-text-grey">
              Upload a TLOG spreadsheet (.xlsx, .xls, or .csv). Rows are grouped by Start Time into
              passes: one S, many T, one C per pass (same as TXT upload).
            </p>

            {file ? (
              <div className="flex items-center p-3 bg-page-bg border border-[#E2E5EA] rounded-lg">
                <FileText className="h-4 w-4 text-heritage-grey mr-2 flex-shrink-0" />
                <span className="text-[13px] text-text-grey flex-1 truncate">{file.name}</span>
                {isProcessing && <span className="text-[12px] text-hero-red mr-2">Processing...</span>}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveFile}
                  disabled={isUploading}
                  className="h-6 w-6 p-0 hover:bg-hero-red-10 hover:text-readable-red"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <FileUpload
                onFileSelect={handleFileSelect}
                accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,application/csv"
                multiple={false}
                buttonText="Upload Excel or CSV"
                variant="outline"
                size="lg"
                className={`w-full ${!isWeldingSystemConfig(machineType) ? 'opacity-60' : ''}`}
                disabled={!isWeldingSystemConfig(machineType)}
              />
            )}

            {parseError && (
              <div className="flex items-start gap-3 p-3 bg-[rgba(196,0,31,0.06)] border border-hero-red-20 rounded-lg text-[13px] text-readable-red">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0 text-readable-red" />
                <span>{parseError}</span>
              </div>
            )}

            {payload && !parseError && (
              <div className="flex items-start gap-3 p-3 bg-[rgba(31,154,58,0.08)] border border-[rgba(31,154,58,0.25)] rounded-lg text-[13px] text-galvanizing-green">
                <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  {excelRowCount} Excel row(s) → {sCount} S + {tCount} T + {cCount} C (one S/C per
                  pass, T per row).
                </span>
              </div>
            )}
          </div>

          {previewRows.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-[13px] font-semibold text-blueprint-blue">Preview</h4>
              <div className="border border-[#E2E5EA] rounded-lg overflow-auto max-h-[240px]">
                <table className="w-full text-[12px]">
                  <thead className="bg-page-bg sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Weld</th>
                      <th className="text-left px-3 py-2">Job</th>
                      <th className="text-left px-3 py-2">Pass</th>
                      <th className="text-left px-3 py-2">Volts</th>
                      <th className="text-left px-3 py-2">Current</th>
                      <th className="text-left px-3 py-2">Travel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, idx) => (
                      <tr key={`${row.weldNumber}-${idx}`} className="border-t border-[#EEF0F3]">
                        <td className="px-3 py-2">{row.weldNumber || '—'}</td>
                        <td className="px-3 py-2">{row.jobNumber || '—'}</td>
                        <td className="px-3 py-2">{row.passName || '—'}</td>
                        <td className="px-3 py-2">{row.volts || '—'}</td>
                        <td className="px-3 py-2">{row.current || '—'}</td>
                        <td className="px-3 py-2">{row.travelSpeed || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-3 px-6 py-4 border-t border-[#E2E5EA] flex-shrink-0">
          <Button variant="outline" onClick={handleClose} disabled={isUploading} className="min-w-[100px]">
            Cancel
          </Button>
          <Button
            onClick={() => void handleUpload()}
            disabled={!payload || isUploading || isProcessing || Boolean(parseError)}
            className="bg-[#EB1C37] hover:bg-[#EB1C37]/90 text-white min-w-[120px]"
            loading={isUploading}
            loadingText="Uploading..."
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
